import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { format, isSameDay } from 'date-fns';
import { zhCN } from 'date-fns/locale';
import {
  CalendarDays,
  CheckSquare,
  Loader2,
  Plus,
  Trash2,
} from 'lucide-react';
import scheduleService from '../../services/scheduleService';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { FLAT_SCRAP_REASONS, findScrapReason } from '../../constants/scrapReasons';

// Editable grid columns use keyboard nav (Enter/Arrow keys move within a column).
const cellInputClass =
  'w-full h-8 px-1 text-center text-sm bg-transparent border border-transparent rounded-none ' +
  'focus:outline-none focus:border-blue-500 focus:bg-blue-50/50';

function DailySettleDialog({ open, onClose, dates, schedules, onConfirm, initialDate, singleDay = false }) {
  const [selectedDate, setSelectedDate] = useState(() => {
    if (singleDay && initialDate) {
      return initialDate;
    }
    if (initialDate && dates.some(d => isSameDay(d, initialDate))) {
      return initialDate;
    }
    return dates[0] || new Date();
  });
  const [loading, setLoading] = useState(false);
  const [itemInputs, setItemInputs] = useState({});
  const inputRefs = useRef({});

  // Sync selectedDate when dialog opens with a new initialDate
  useEffect(() => {
    if (!open) return;
    if (singleDay && initialDate) {
      setSelectedDate(initialDate);
      setItemInputs({});
    } else if (initialDate && dates.some(d => isSameDay(d, initialDate))) {
      setSelectedDate(initialDate);
      setItemInputs({});
    } else if (dates.length > 0) {
      setSelectedDate(dates[0]);
      setItemInputs({});
    }
  }, [open, singleDay, initialDate, dates]);

  // When settling a historical date outside the current week, fetch its schedules
  const isHistoricalDate = singleDay && initialDate && !dates.some(d => isSameDay(d, initialDate));
  const [historicalSchedules, setHistoricalSchedules] = useState([]);
  const [historicalLoading, setHistoricalLoading] = useState(false);

  useEffect(() => {
    if (!open || !isHistoricalDate || !initialDate) {
      setHistoricalSchedules([]);
      return;
    }

    let cancelled = false;
    setHistoricalLoading(true);
    const dateStr = format(initialDate, 'yyyy-MM-dd');
    scheduleService.getSchedules({ start_date: dateStr, end_date: dateStr })
      .then((data) => {
        if (!cancelled) {
          setHistoricalSchedules(data?.schedules || []);
        }
      })
      .catch((err) => {
        console.error('Failed to fetch historical schedules:', err);
        if (!cancelled) setHistoricalSchedules([]);
      })
      .finally(() => {
        if (!cancelled) setHistoricalLoading(false);
      });

    return () => { cancelled = true; };
  }, [open, isHistoricalDate, initialDate]);

  const effectiveSchedules = useMemo(() => {
    if (isHistoricalDate) {
      return historicalSchedules;
    }
    return schedules;
  }, [isHistoricalDate, historicalSchedules, schedules]);

  const settleData = useMemo(() => {
    const datesToRender = [...dates];
    // In single-day mode, ensure initialDate is included even if it's outside the current week
    if (singleDay && initialDate && !datesToRender.some(d => isSameDay(d, initialDate))) {
      datesToRender.push(initialDate);
    }

    const result = datesToRender.map((date) => {
      const daySchedules = effectiveSchedules.filter((s) => {
        const schDate = new Date(s.scheduled_date);
        return isSameDay(schDate, date);
      });
      const uncompleted = daySchedules.filter(
        (s) => !['completed', 'rolled_over', 'cancelled'].includes(s.status)
      );
      const completed = daySchedules.filter((s) => s.status === 'completed');
      return {
        date,
        total: daySchedules.length,
        uncompleted: uncompleted.length,
        completed: completed.length,
        uncompletedItems: uncompleted,
      };
    });
    return result;
  }, [dates, effectiveSchedules, singleDay, initialDate]);

  const selectedDay = settleData.find((d) => isSameDay(d.date, selectedDate));

  // Reset inputs when date changes
  const handleSelectDate = useCallback((date) => {
    setSelectedDate(date);
    setItemInputs({});
  }, []);

  const handleInputChange = (scheduleId, field, value) => {
    setItemInputs((prev) => {
      const updated = {
        ...prev,
        [scheduleId]: {
          ...prev[scheduleId],
          [field]: value,
        },
      };
      // Keep scrap reasons in sync with the scrapped quantity:
      // 0 clears them; a single reason auto-tracks the quantity so the common
      // case (one reason for all scraps) needs no extra input. Once the user
      // splits into multiple reasons, quantities become manual.
      if (field === 'scrapped_quantity') {
        const qty = parseInt(value || '0', 10) || 0;
        const reasons = prev[scheduleId]?.scrap_reasons || [];
        if (qty === 0) {
          updated[scheduleId].scrap_reasons = [];
        } else if (reasons.length === 0) {
          updated[scheduleId].scrap_reasons = [{ reason_code: '', quantity: qty }];
        } else if (reasons.length === 1) {
          updated[scheduleId].scrap_reasons = [{ ...reasons[0], quantity: qty }];
        }
      }
      return updated;
    });
  };

  const addScrapReason = (scheduleId) => {
    setItemInputs((prev) => {
      const input = prev[scheduleId] || {};
      const scrapped = parseInt(input.scrapped_quantity || '0', 10) || 0;
      const reasons = input.scrap_reasons || [];
      const allocated = reasons.reduce((sum, r) => sum + (parseInt(r.quantity || '0', 10) || 0), 0);
      return {
        ...prev,
        [scheduleId]: {
          ...input,
          scrap_reasons: [
            ...reasons,
            { reason_code: '', quantity: Math.max(0, scrapped - allocated) },
          ],
        },
      };
    });
  };

  const updateScrapReason = (scheduleId, index, field, value) => {
    setItemInputs((prev) => {
      const reasons = [...(prev[scheduleId]?.scrap_reasons || [])];
      reasons[index] = { ...reasons[index], [field]: value };
      return {
        ...prev,
        [scheduleId]: {
          ...prev[scheduleId],
          scrap_reasons: reasons,
        },
      };
    });
  };

  const removeScrapReason = (scheduleId, index) => {
    setItemInputs((prev) => {
      const reasons = [...(prev[scheduleId]?.scrap_reasons || [])];
      reasons.splice(index, 1);
      // Back to a single reason: re-sync its quantity with the scrapped qty.
      if (reasons.length === 1) {
        const scrapped = parseInt(prev[scheduleId]?.scrapped_quantity || '0', 10) || 0;
        reasons[0] = { ...reasons[0], quantity: scrapped };
      }
      return {
        ...prev,
        [scheduleId]: {
          ...prev[scheduleId],
          scrap_reasons: reasons,
        },
      };
    });
  };

  // Excel-like keyboard navigation: Enter/ArrowDown moves to the next row in
  // the same column, ArrowUp moves up.
  const handleCellKeyDown = (e, rowIndex, field) => {
    let targetRow = null;
    if (e.key === 'Enter' || e.key === 'ArrowDown') {
      targetRow = rowIndex + 1;
    } else if (e.key === 'ArrowUp') {
      targetRow = rowIndex - 1;
    } else {
      return;
    }
    e.preventDefault();
    const el = inputRefs.current[`${targetRow}:${field}`];
    if (el) {
      el.focus();
      el.select?.();
    }
  };

  const setCellRef = (rowIndex, field) => (el) => {
    const key = `${rowIndex}:${field}`;
    if (el) {
      inputRefs.current[key] = el;
    } else {
      delete inputRefs.current[key];
    }
  };

  const rows = useMemo(() => {
    if (!selectedDay) return [];
    return selectedDay.uncompletedItems.map((item) => {
      const input = itemInputs[item._id] || {};
      const prevCompleted = item.completed_quantity || 0;
      const inputCompleted = parseInt(input.completed_quantity || '0', 10) || 0;
      const inputScrapped = parseInt(input.scrapped_quantity || '0', 10) || 0;
      // Scraps must be re-made, so they count against remaining work.
      const remaining = item.assigned_quantity - prevCompleted - inputCompleted - inputScrapped;
      const allDone = remaining <= 0;
      const reasons = input.scrap_reasons || [];
      const allocated = reasons.reduce((sum, r) => sum + (parseInt(r.quantity || '0', 10) || 0), 0);
      // Scrapped qty must be fully explained: every reason needs a code and
      // the reason quantities must add up to the scrapped quantity.
      const scrapError =
        inputScrapped > 0 &&
        (reasons.length === 0 ||
          reasons.some((r) => !r.reason_code) ||
          allocated !== inputScrapped);
      return {
        item,
        input,
        reasons,
        allocated,
        prevCompleted,
        inputScrapped,
        remaining,
        allDone,
        scrapError,
      };
    });
  }, [selectedDay, itemInputs]);

  const hasValidationError = rows.some((r) => r.scrapError);

  const handleConfirm = async () => {
    if (!selectedDay) return;
    const items = selectedDay.uncompletedItems.map((item) => {
      const input = itemInputs[item._id] || {};
      const scrappedQty = parseInt(input.scrapped_quantity || '0', 10) || 0;
      const scrapReasons = scrappedQty > 0
        ? (input.scrap_reasons || [])
            .filter((r) => r.reason_code && (parseInt(r.quantity || '0', 10) || 0) > 0)
            .map((r) => {
              const reason = findScrapReason(r.reason_code);
              return {
                reason_code: r.reason_code,
                category: reason?.category || '',
                item: reason?.name || '',
                quantity: parseInt(r.quantity || '0', 10) || 0,
              };
            })
        : [];
      return {
        schedule_id: item._id,
        completed_quantity: parseInt(input.completed_quantity || '0', 10) || 0,
        scrapped_quantity: scrappedQty,
        scrap_reasons: scrapReasons,
        notes: input.notes || '',
      };
    });

    setLoading(true);
    try {
      await onConfirm(selectedDate, items);
      setItemInputs({});
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="w-[calc(100%-2rem)] sm:max-w-6xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckSquare className="h-4 w-4" />
            {singleDay && selectedDay
              ? format(selectedDay.date, 'yyyy年MM月dd日 结算', { locale: zhCN })
              : '日结算'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 py-2">
          {!singleDay && (
            <>
              <div className="text-sm text-gray-600">
                选择要结算的日期，在表格中填写当日完成和报废数量。报废必须选择原因。未完成的将自动加塞到次日。
              </div>

              {/* Date selector */}
              <div className="grid grid-cols-4 sm:grid-cols-7 gap-1">
                {settleData.map((day) => (
                  <button
                    key={day.date.toISOString()}
                    onClick={() => handleSelectDate(day.date)}
                    className={`
                      p-2 rounded-md text-center text-xs transition-colors
                      ${isSameDay(day.date, selectedDate)
                        ? 'bg-blue-100 border border-blue-300 text-blue-800'
                        : 'bg-gray-50 border border-gray-200 hover:bg-gray-100 text-gray-700'}
                    `}
                  >
                    <div className="font-semibold">{format(day.date, 'MM/dd')}</div>
                    <div className="text-[10px] mt-0.5">
                      {day.uncompleted > 0 ? (
                        <span className="text-amber-600">{day.uncompleted}待结</span>
                      ) : (
                        <span className="text-green-600">已结清</span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}

          {historicalLoading ? (
            <div className="flex items-center justify-center py-12 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              加载该日期排程数据...
            </div>
          ) : selectedDay ? (
            <div className="border rounded-md bg-white">
              <div className="flex items-center gap-2 px-3 py-2 border-b bg-gray-50">
                <CalendarDays className="h-4 w-4 text-gray-500" />
                <span className="text-sm font-medium">
                  {format(selectedDay.date, 'yyyy年MM月dd日', { locale: zhCN })}
                </span>
                <span className="text-xs text-gray-400">
                  共 {selectedDay.uncompletedItems.length} 个待结算任务
                </span>
                {hasValidationError && (
                  <span className="ml-auto text-xs text-red-600 font-medium">
                    有报废未选择原因
                  </span>
                )}
              </div>

              {selectedDay.uncompleted > 0 ? (
                <div className="max-h-[52vh] overflow-auto">
                  <table className="w-full border-collapse text-sm">
                    <thead className="sticky top-0 z-10">
                      <tr className="bg-gray-100 text-xs text-gray-600">
                        <th className="border border-gray-200 px-2 py-1.5 font-semibold text-left w-[130px]">货号</th>
                        <th className="border border-gray-200 px-2 py-1.5 font-semibold text-left">产品名称</th>
                        <th className="border border-gray-200 px-2 py-1.5 font-semibold text-left w-[130px]">工单号</th>
                        <th className="border border-gray-200 px-2 py-1.5 font-semibold text-center w-[110px]">设备</th>
                        <th className="border border-gray-200 px-2 py-1.5 font-semibold text-left w-[90px]">工序</th>
                        <th className="border border-gray-200 px-1 py-1.5 font-semibold text-center w-[60px]">分配</th>
                        <th className="border border-gray-200 px-1 py-1.5 font-semibold text-center w-[60px]">已完</th>
                        <th className="border border-gray-200 px-1 py-1.5 font-semibold text-center w-[76px] bg-blue-50 text-blue-800">今日完成</th>
                        <th className="border border-gray-200 px-1 py-1.5 font-semibold text-center w-[76px] bg-red-50 text-red-800">今日报废</th>
                        <th className="border border-gray-200 px-1 py-1.5 font-semibold text-center w-[250px] bg-red-50 text-red-800">报废原因</th>
                        <th className="border border-gray-200 px-1 py-1.5 font-semibold text-center w-[76px]">剩余</th>
                        <th className="border border-gray-200 px-1 py-1.5 font-semibold text-left w-[140px]">备注</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row, rowIndex) => {
                        const { item, input, reasons, allocated, prevCompleted, inputScrapped, remaining, allDone, scrapError } = row;
                        return (
                          <tr
                            key={item._id}
                            className={allDone ? 'bg-green-50/60' : rowIndex % 2 === 1 ? 'bg-gray-50/50' : 'bg-white'}
                          >
                            <td className="border border-gray-200 px-2 py-0.5">
                              <span className="font-bold text-[15px] text-gray-900 tracking-wide">
                                {item.finished_product_code || '-'}
                              </span>
                            </td>
                            <td className="border border-gray-200 px-2 py-0.5 text-xs text-gray-600">
                              {item.finished_product_name || ''}
                            </td>
                            <td className="border border-gray-200 px-2 py-0.5 text-xs font-medium text-gray-700 whitespace-nowrap">
                              {item.production_order_number || ''}
                            </td>
                            <td className="border border-gray-200 px-2 py-0.5 text-center">
                              <Badge className="bg-indigo-100 text-indigo-800 hover:bg-indigo-100 border border-indigo-300 font-bold text-xs px-2">
                                {item.work_center_name || '-'}
                              </Badge>
                            </td>
                            <td className="border border-gray-200 px-2 py-0.5 text-xs font-medium text-gray-700">
                              {item.step_name}
                            </td>
                            <td className="border border-gray-200 px-1 py-0.5 text-center font-semibold text-gray-800">
                              {item.assigned_quantity}
                            </td>
                            <td className="border border-gray-200 px-1 py-0.5 text-center text-xs text-gray-500">
                              {prevCompleted > 0 ? prevCompleted : ''}
                            </td>
                            <td className={`border px-0 py-0 ${allDone ? 'border-green-300' : 'border-gray-200'}`}>
                              <input
                                ref={setCellRef(rowIndex, 'completed_quantity')}
                                type="number"
                                min="0"
                                className={`${cellInputClass} font-semibold text-blue-800`}
                                value={input.completed_quantity || ''}
                                placeholder="0"
                                onFocus={(e) => e.target.select()}
                                onChange={(e) => handleInputChange(item._id, 'completed_quantity', e.target.value)}
                                onKeyDown={(e) => handleCellKeyDown(e, rowIndex, 'completed_quantity')}
                              />
                            </td>
                            <td className={`border px-0 py-0 ${inputScrapped > 0 ? 'border-red-300 bg-red-50/40' : 'border-gray-200'}`}>
                              <input
                                ref={setCellRef(rowIndex, 'scrapped_quantity')}
                                type="number"
                                min="0"
                                className={`${cellInputClass} font-semibold ${inputScrapped > 0 ? 'text-red-700' : 'text-gray-600'}`}
                                value={input.scrapped_quantity || ''}
                                placeholder="0"
                                onFocus={(e) => e.target.select()}
                                onChange={(e) => handleInputChange(item._id, 'scrapped_quantity', e.target.value)}
                                onKeyDown={(e) => handleCellKeyDown(e, rowIndex, 'scrapped_quantity')}
                              />
                            </td>
                            <td className={`border px-1 py-0.5 align-top ${scrapError ? 'border-red-400 bg-red-50' : 'border-gray-200'}`}>
                              {inputScrapped > 0 ? (
                                <div className="space-y-1 py-0.5">
                                  {reasons.map((reason, idx) => (
                                    <div key={idx} className="flex items-center gap-1">
                                      <Select
                                        value={reason.reason_code || ''}
                                        onValueChange={(value) => updateScrapReason(item._id, idx, 'reason_code', value)}
                                      >
                                        <SelectTrigger
                                          className={`h-7 flex-1 min-w-0 text-xs border-0 shadow-none rounded-none px-1 focus:ring-1 focus:ring-blue-500 ${!reason.reason_code ? 'text-red-500' : ''}`}
                                        >
                                          <SelectValue placeholder="必选：报废原因" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {FLAT_SCRAP_REASONS.map((r) => (
                                            <SelectItem key={r.code} value={r.code} className="text-xs">
                                              {r.label}
                                            </SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                      {reasons.length > 1 ? (
                                        <>
                                          <input
                                            type="number"
                                            min="1"
                                            max={inputScrapped}
                                            className="w-12 h-7 px-1 text-center text-xs border border-gray-200 rounded focus:outline-none focus:border-blue-500"
                                            value={reason.quantity || ''}
                                            onFocus={(e) => e.target.select()}
                                            onChange={(e) => updateScrapReason(item._id, idx, 'quantity', e.target.value)}
                                          />
                                          <button
                                            type="button"
                                            onClick={() => removeScrapReason(item._id, idx)}
                                            className="text-gray-400 hover:text-red-500 shrink-0"
                                            title="删除该原因"
                                          >
                                            <Trash2 className="h-3.5 w-3.5" />
                                          </button>
                                        </>
                                      ) : (
                                        <span className="w-12 shrink-0 text-center text-[10px] text-gray-400">
                                          ×{parseInt(reason.quantity || '0', 10) || 0}
                                        </span>
                                      )}
                                    </div>
                                  ))}
                                  <div className="flex items-center justify-between gap-1">
                                    <button
                                      type="button"
                                      onClick={() => addScrapReason(item._id)}
                                      className="flex items-center text-[10px] text-blue-600 hover:text-blue-800"
                                    >
                                      <Plus className="h-3 w-3 mr-0.5" />
                                      添加原因
                                    </button>
                                    {allocated !== inputScrapped && (
                                      <span className="text-[10px] text-amber-600 whitespace-nowrap">
                                        已分配 {allocated}/{inputScrapped}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              ) : (
                                <span className="block text-center text-[10px] text-gray-300">—</span>
                              )}
                            </td>
                            <td className="border border-gray-200 px-1 py-0.5 text-center">
                              <span className={`text-xs font-semibold ${allDone ? 'text-green-700' : 'text-amber-700'}`}>
                                {allDone ? '✓ 完成' : remaining}
                              </span>
                            </td>
                            <td className="border border-gray-200 px-0 py-0">
                              <input
                                ref={setCellRef(rowIndex, 'notes')}
                                type="text"
                                className={`${cellInputClass} text-left text-xs`}
                                value={input.notes || ''}
                                placeholder=""
                                onChange={(e) => handleInputChange(item._id, 'notes', e.target.value)}
                                onKeyDown={(e) => handleCellKeyDown(e, rowIndex, 'notes')}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-sm text-green-700 bg-green-50 m-3 p-2 rounded">
                  当天所有任务已完成或已结算，无需操作。
                </div>
              )}
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            取消
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={
              !selectedDay ||
              selectedDay.uncompleted === 0 ||
              loading ||
              historicalLoading ||
              hasValidationError
            }
          >
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            确认结算
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default DailySettleDialog;
